import { base64url, canonical, newDataKey, openObject, sealObject, unbase64url } from "./cryptoV2";

export type PairTicket = {
  version: 2;
  id: string;
  secret: string;
  expiresAt: number;
  direction: "new-displays" | "authorized-displays";
};
// The service still expires and consumes the relay at exactly five minutes.
// Allow the same small clock skew as signed control requests when parsing a
// server-issued deadline; never replace or extend that deadline locally.
const MAX_FUTURE_DEADLINE = 300000 + 60000;
export async function createPairTicket(
  id: string,
  expiresAt: number,
  direction: PairTicket["direction"],
): Promise<PairTicket> {
  if (
    !/^[\w-]{16,128}$/.test(id) ||
    !Number.isSafeInteger(expiresAt) ||
    expiresAt <= Date.now() ||
    expiresAt > Date.now() + MAX_FUTURE_DEADLINE
  )
    throw new Error("sync-pair-expired");
  return { version: 2, id, expiresAt, direction, secret: newDataKey() };
}
export function pairQr(ticket: PairTicket, origin: string) {
  if (new URL(origin).protocol !== "https:" && new URL(origin).hostname !== "localhost")
    throw new Error("sync-pair-origin");
  // Fragments are never sent in HTTP requests or the service relay. Do not put
  // this URL in telemetry, router state, browser screenshots or server logs.
  return `${new URL(origin).origin}/sync-pair#${base64url(new TextEncoder().encode(canonical(ticket)))}`;
}
export function readPairQr(url: string, origin: string, now = Date.now()): PairTicket {
  const parsed = new URL(url);
  if (parsed.origin !== new URL(origin).origin || parsed.pathname !== "/sync-pair") throw new Error("sync-pair-origin");
  const ticket = JSON.parse(
    new TextDecoder("utf-8", { fatal: true }).decode(unbase64url(parsed.hash.slice(1), 2048)),
  ) as PairTicket;
  if (
    !ticket ||
    Object.keys(ticket).sort().join() !== "direction,expiresAt,id,secret,version" ||
    !Number.isSafeInteger(ticket.expiresAt) ||
    ticket.version !== 2 ||
    !/^[\w-]{16,128}$/.test(ticket.id) ||
    !["new-displays", "authorized-displays"].includes(ticket.direction) ||
    unbase64url(ticket.secret, 32).length !== 32 ||
    ticket.expiresAt <= now ||
    ticket.expiresAt > now + MAX_FUTURE_DEADLINE
  )
    throw new Error("sync-pair-expired");
  return ticket;
}
const scope = (ticket: PairTicket, kind: "request" | "approval") => ({
  datasetId: ticket.id,
  generation: ticket.id,
  keyVersion: 1,
  objectId: kind,
  format: 2 as const,
});
export async function sealPairMessage(ticket: PairTicket, kind: "request" | "approval", value: unknown) {
  if (ticket.expiresAt <= Date.now()) throw new Error("sync-pair-expired");
  return sealObject(ticket.secret, scope(ticket, kind), value);
}
export async function openPairMessage(
  ticket: PairTicket,
  kind: "request" | "approval",
  cipher: Awaited<ReturnType<typeof sealPairMessage>>,
) {
  if (ticket.expiresAt <= Date.now()) throw new Error("sync-pair-expired");
  return openObject(ticket.secret, scope(ticket, kind), cipher);
}
